import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle, Download, Search, LogOut, AlertTriangle, Users, Settings, Mail, History, Percent, BarChart3, FileDown, User, FileSignature, RefreshCw, MapPin, Inbox, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';
import NotificationCenter from '../components/NotificationCenter';
import PendingAgreementsIndicator from '../components/PendingAgreementsIndicator';
import ThemeToggle from '../components/admin/ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { LOGO_DARK_BG } from '../config/branding';

const useAdminAuth = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
    }
  }, [token, navigate]);
  
  return token;
};

const AdminDashboardPage = () => {
  const token = useAdminAuth();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [pendingActions, setPendingActions] = useState(null);
  const [whatsappStats, setWhatsappStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('eingegangen'); // DEFAULT: eingegangen
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, statusFilter, searchTerm]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh || !token) return;
    
    const interval = setInterval(() => {
      loadData(true); // silent refresh
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, token, statusFilter, searchTerm]);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const statsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData);

      // Load pending actions
      try {
        const pendingRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pending-actions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          setPendingActions(pendingData);
        }
      } catch (e) { console.error('Pending actions error:', e); }

      // Load WhatsApp stats
      try {
        const waRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/whatsapp/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (waRes.ok) {
          const waData = await waRes.json();
          setWhatsappStats(waData);
        }
      } catch (e) { console.error('WhatsApp stats error:', e); }

      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const casesRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cases?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const casesData = await casesRes.json();
      setCases(Array.isArray(casesData) ? casesData : []);
      
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-csv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
    } catch (error) {
      alert('Export fehlgeschlagen');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-700',
      eingegangen: 'bg-blue-100 text-blue-700',
      in_pruefung: 'bg-indigo-100 text-indigo-700',
      rueckfrage_offen: 'bg-orange-100 text-orange-700',
      termin_ausstehend: 'bg-purple-100 text-purple-700',
      termin_geplant: 'bg-purple-200 text-purple-800',
      begutachtung_laeuft: 'bg-cyan-100 text-cyan-700',
      gutachten_in_erstellung: 'bg-yellow-100 text-yellow-700',
      gutachten_fertig: 'bg-teal-100 text-teal-700',
      abgeschlossen: 'bg-brand-green/10 text-brand-green'
    };
    return badges[status] || 'bg-gray-100 text-gray-600';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Entwurf',
      eingegangen: 'Eingegangen',
      in_pruefung: 'In Prüfung',
      rueckfrage_offen: 'Rückfrage offen',
      termin_ausstehend: 'Termin ausstehend',
      termin_geplant: 'Termin geplant',
      begutachtung_laeuft: 'Begutachtung läuft',
      gutachten_in_erstellung: 'Gutachten in Erstellung',
      gutachten_fertig: 'Gutachten fertig',
      abgeschlossen: 'Abgeschlossen'
    };
    return labels[status] || status;
  };

  if (loading) {
    return <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>Loading...</div>;
  }

  return (
    <div className={`min-h-screen admin-page ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className={`backdrop-blur-md border-b ${isDarkMode ? 'bg-slate-950 border-slate-700' : 'bg-black/90 border-white/10'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_DARK_BG} alt="Dein Gutachten Direkt" className="h-10" />
            <h1 className="text-xl font-bold text-white">Admin Portal</h1>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <PendingAgreementsIndicator token={token} />
            <NotificationCenter token={token} />
            <Button onClick={() => navigate('/admin/profile')} variant="outline" className="border-white/20 text-white hover:bg-white/10" size="sm">
              <User className="w-4 h-4 mr-2" />
              Profil
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-white/20 text-white hover:bg-white/10" size="sm">
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-5 gap-6 mb-12">
          <div className={`border rounded-2xl p-6 admin-card ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <FileText className={`w-6 h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Eingegangen</p>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats?.eingegangen || 0}</p>
              </div>
            </div>
          </div>

          <div className={`border rounded-2xl p-6 admin-card ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">In Bearbeitung</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.in_bearbeitung || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Abgeschlossen</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.abgeschlossen || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">30 Tage</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.recent_30_days || 0}</p>
              </div>
            </div>
          </div>

          {/* WhatsApp Stats Card */}
          <div 
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 cursor-pointer hover:from-green-600 hover:to-green-700 transition-all"
            onClick={() => navigate('/admin/whatsapp')}
            data-testid="whatsapp-stats-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-white/80">WhatsApp</p>
                <p className="text-2xl font-bold text-white">{whatsappStats?.last_24_hours || 0}</p>
                <p className="text-xs text-white/70">Nachrichten (24h)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Actions Alert Bar */}
        {pendingActions && (pendingActions.open_inquiries > 0 || pendingActions.stale_cases > 0 || pendingActions.unsigned_agreements > 0) && (
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-4 mb-6 text-white" data-testid="pending-actions-widget">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-bold">Ausstehende Aktionen</span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                {pendingActions.open_inquiries > 0 && (
                  <button 
                    onClick={() => setStatusFilter('rueckfrage_offen')}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <span className="w-6 h-6 bg-white text-orange-600 rounded-full flex items-center justify-center font-bold text-xs">
                      {pendingActions.open_inquiries}
                    </span>
                    <span>Offene Rückfragen</span>
                  </button>
                )}
                {pendingActions.stale_cases > 0 && (
                  <div className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-1.5">
                    <span className="w-6 h-6 bg-white text-red-600 rounded-full flex items-center justify-center font-bold text-xs">
                      {pendingActions.stale_cases}
                    </span>
                    <span>Fälle ohne Update (7+ Tage)</span>
                  </div>
                )}
                {pendingActions.unsigned_agreements > 0 && (
                  <button
                    onClick={() => navigate('/admin/vereinbarungen')}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <span className="w-6 h-6 bg-white text-orange-600 rounded-full flex items-center justify-center font-bold text-xs">
                      {pendingActions.unsigned_agreements}
                    </span>
                    <span>Vereinbarungen ausstehend</span>
                  </button>
                )}
                {pendingActions.expiring_trials > 0 && (
                  <button
                    onClick={() => navigate('/admin/trial-management')}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <span className="w-6 h-6 bg-white text-purple-600 rounded-full flex items-center justify-center font-bold text-xs">
                      {pendingActions.expiring_trials}
                    </span>
                    <span>Trial läuft ab</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <button
            onClick={() => navigate('/admin/touren')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Vertrieb-Touren</h3>
              <p className="text-sm text-gray-600">Touren planen & verwalten</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/payouts')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Auszahlungen</h3>
              <p className="text-sm text-gray-600">Partner-Provisionen verwalten</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/vertriebsteam')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Vertriebsteam</h3>
              <p className="text-sm text-gray-600">Vertriebler einladen & verwalten</p>
            </div>
          </button>
          
          <button
            onClick={() => window.open('/vertrieb/praesentation', '_blank')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Präsentation</h3>
              <p className="text-sm text-gray-600">Vertrieb-Präsentation öffnen</p>
            </div>
          </button>
          <button
            onClick={() => navigate('/admin/trial-management')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Trial-Partner</h3>
              <p className="text-sm text-gray-600">Ablaufende Testphasen</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/partner-einstellungen')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Percent className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Partner-Level</h3>
              <p className="text-sm text-gray-600">Provisionen verwalten</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/vereinbarungen')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
            data-testid="agreements-link"
          >
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <FileSignature className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Vereinbarungen</h3>
              <p className="text-sm text-gray-600">Partnerverträge verwalten</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/email-templates')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">E-Mail Templates</h3>
              <p className="text-sm text-gray-600">Vorlagen bearbeiten</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/audit-log')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
              <History className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Audit-Log</h3>
              <p className="text-sm text-gray-600">Aktivitäten ansehen</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/einstellungen')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Einstellungen</h3>
              <p className="text-sm text-gray-600">System-Konfiguration</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/analytics')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Analytics</h3>
              <p className="text-sm text-gray-600">Statistiken & Grafiken</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/crm')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
            data-testid="crm-link"
          >
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">CRM</h3>
              <p className="text-sm text-gray-600">Kontakte & Blacklist</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/auto-assign')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
            data-testid="auto-assign-link"
          >
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Auto-Zuweisung</h3>
              <p className="text-sm text-gray-600">Standorte & Fallzuweisung</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/email-import')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
            data-testid="email-import-link"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Inbox className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">E-Mail Import</h3>
              <p className="text-sm text-gray-600">Ultra Expert Gutachten</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/chats')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
            data-testid="chats-link"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Kunden-Chats</h3>
              <p className="text-sm text-gray-600">KI-Bot & Support</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/whatsapp')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
            data-testid="whatsapp-link"
          >
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">WhatsApp</h3>
              <p className="text-sm text-gray-600">Kunden-Benachrichtigungen</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/reports')}
            className="bg-white border-2 border-gray-200 hover:border-brand-green rounded-2xl p-5 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
              <FileDown className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">PDF Reports</h3>
              <p className="text-sm text-gray-600">Monats- & Partner-Berichte</p>
            </div>
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Suche (Kennzeichen, Ort, Partner...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              <option value="">Alle Status</option>
              <option value="eingegangen">Eingegangen</option>
              <option value="in_pruefung">In Prüfung</option>
              <option value="rueckfrage_offen">Rückfrage offen</option>
              <option value="termin_ausstehend">Termin ausstehend</option>
              <option value="termin_geplant">Termin geplant</option>
              <option value="begutachtung_laeuft">Begutachtung läuft</option>
              <option value="gutachten_in_erstellung">Gutachten in Erstellung</option>
              <option value="gutachten_fertig">Gutachten fertig</option>
              <option value="abgeschlossen">Abgeschlossen</option>
            </select>
            <Button onClick={handleExport} variant="outline" className="border-2 border-gray-300">
              <Download className="w-4 h-4 mr-2" />
              CSV Export
            </Button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Datum</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Kennzeichen</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Schadenort</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Partner</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Level</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cases.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      Keine Fälle gefunden
                    </td>
                  </tr>
                ) : (
                  cases.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {c.submitted_at ? new Date(c.submitted_at).toLocaleDateString('de-DE') : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {c.license_plate || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {c.damage_location || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {c.partner_company || c.partner_email || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(c.status)}`}>
                          {getStatusLabel(c.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-brand-green">
                        {c.partner_provision_level}%
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          onClick={() => {
                            console.log('Opening case:', c.id);
                            navigate(`/admin/cases/${c.id}`);
                          }}
                          size="sm"
                          variant="outline"
                          className="border-gray-300"
                          data-testid={`open-case-${c.id}`}
                        >
                          Öffnen
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Version Footer with Auto-Refresh */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span className="text-xs text-gray-400">Admin Dashboard • {getVersionString()}</span>
          <div className="flex items-center gap-3">
            {lastRefreshed && (
              <span className="text-xs text-gray-500">
                Aktualisiert: {lastRefreshed.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors ${
                autoRefresh 
                  ? 'bg-green-900/50 text-green-400 hover:bg-green-900/70' 
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
              title={autoRefresh ? 'Auto-Refresh aktiv (30s)' : 'Auto-Refresh deaktiviert'}
              data-testid="auto-refresh-toggle"
            >
              <RefreshCw className={`w-3 h-3 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
              {autoRefresh ? 'Auto' : 'Manuell'}
            </button>
            <button
              onClick={() => loadData()}
              className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
            >
              Jetzt aktualisieren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
